<!--
  SelectReferenceField — single-value store-backed reference renderer (schema /
  type / user); the chrome-free inner GridCell hosts. A keyboard-navigable
  dropdown (options from the schema/type/user stores). `modelValue` is the
  resolved entities (one element). inline/active show the label + chevron; editing
  shows the dropdown. Picking emits the chosen ENTITY (`{ id }`) + commit.
-->
<template>
  <div class="select-reference-field">
    <template v-if="mode !== 'editing'">
      <span class="select-value" :class="sizeClass">{{ displayLabel }}</span>
      <span class="select-chevron icon is-small">
        <font-awesome-icon icon="chevron-down" size="xs" />
      </span>
    </template>
    <div
      v-else
      ref="dropdownRef"
      class="select-dropdown"
      tabindex="-1"
      @keydown="onDropdownKeydown"
      @mousedown.prevent
      @click.stop
    >
      <template v-for="group in groups" :key="group.key">
        <div v-if="group.label" class="select-group-label" :class="sizeClass">
          {{ group.label }}
        </div>
        <div
          v-for="opt in group.options"
          :key="opt.value"
          class="select-option"
          :class="[
            sizeClass,
            {
              'is-selected': opt.value === currentValue,
              'is-highlighted': indexByValue.get(opt.value) === highlightedIndex,
            },
          ]"
          @click.stop="selectValue(opt.value)"
          @mouseenter="highlightedIndex = indexByValue.get(opt.value) ?? highlightedIndex"
        >
          {{ opt.label }}
        </div>
      </template>
      <div v-if="flatOptions.length === 0" class="select-empty" :class="sizeClass">
        No options available
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue"
import { storeToRefs } from "pinia"
import type { AugerField } from "@auger/core/fields"
import { type FieldMode, type FieldSize, textSizeClass } from "@auger/ui/fields/useField"
import { useSchemasStore } from "@/stores/schemas"
import { useTypesStore } from "@/stores/types"
import { useUsersStore } from "@/stores/users"

type OptionValue = number | string
/** The "None" option's value: picking it commits an empty reference. */
const NONE = ""
interface SelectOption {
  value: OptionValue
  label: string
}
interface OptionGroup {
  key: string
  label: string | null
  options: SelectOption[]
}
type ReferenceEntity = { id: OptionValue; name?: unknown; label?: unknown; [k: string]: unknown }

const props = withDefaults(
  defineProps<{
    modelValue: unknown
    field: AugerField
    mode: FieldMode
    size?: FieldSize
    /** Per-row record type for the polymorphic `schema_id` picker (GridCell derives
     *  it from the row); falls back to the field's fixed `recordType`. */
    recordType?: string | null
  }>(),
  { size: "small", recordType: null }
)

const emit = defineEmits<{
  "update:modelValue": [value: unknown]
  commit: [payload?: { advance?: boolean }]
  cancel: []
}>()

const sizeClass = computed(() => textSizeClass(props.size))

// The current entity (single-select → the sole element) and its stored id
// (schema/user id, or type name); the field shows its label.
const currentEntity = computed<ReferenceEntity | null>(() => {
  const entities = props.modelValue as ReferenceEntity[] | null | undefined
  return entities?.[0] ?? null
})
const currentValue = computed<OptionValue | null>(() => currentEntity.value?.id ?? null)

// OPTIONS =====================================================================
// Ported from ReferenceSelect: grouped options per reference type, sourced from
// the store. Rendered as the custom dropdown below rather than a native <select>.

const { schemas } = storeToRefs(useSchemasStore())
const typesStore = useTypesStore()
const usersStore = useUsersStore()
const { users } = storeToRefs(usersStore)

// Scope the schema picker to one record type and drop that type's base schema
// (its fields are inherited automatically). The record type comes from
// `field.recordType` when the field fixes a type.
const schemaRecordType = computed<string | null>(
  () => props.recordType ?? props.field.recordType ?? null
)

const schemaTypeSchemaId = computed(() => {
  const recordType = schemaRecordType.value
  if (!recordType) return null
  return typesStore.get(recordType)?.schema_id ?? null
})

const schemaGroups = computed<OptionGroup[]>(() => {
  const recordType = schemaRecordType.value
  if (recordType) {
    const filtered = schemas.value.filter(
      (s) => s.record_type === recordType && s.id !== schemaTypeSchemaId.value
    )
    return [
      {
        key: "schemas",
        label: null,
        options: filtered.map((s) => ({ value: s.id, label: s.name })),
      },
    ]
  }
  const byType = new Map<string, typeof schemas.value>()
  for (const schema of schemas.value) {
    const t = schema.record_type || "untyped"
    if (!byType.has(t)) byType.set(t, [])
    byType.get(t)!.push(schema)
  }
  const result: OptionGroup[] = []
  for (const type of typesStore.types) {
    const list = byType.get(type.name)
    if (!list?.length) continue
    result.push({
      key: type.name,
      label: type.plural_label,
      options: list.map((s) => ({ value: s.id, label: s.name })),
    })
  }
  const untyped = byType.get("untyped")
  if (untyped?.length) {
    result.push({
      key: "untyped",
      label: "Untyped Schemas",
      options: untyped.map((s) => ({ value: s.id, label: s.name })),
    })
  }
  return result
})

const typeGroups = computed<OptionGroup[]>(() => [
  {
    key: "types",
    label: null,
    options: typesStore.types.map((t) => ({ value: t.name, label: t.label })),
  },
])

// The staff directory (person ids), split into Partners / Staff, each ordered
// alphabetically by label. The directory holds active members only, so the
// Deactivated group stays empty unless a host supplies inactive entries.
const userGroups = computed<OptionGroup[]>(() => {
  const toOpt = (u: Record<string, unknown>): SelectOption => ({
    value: u.id as number,
    label: (u.name as string) || (u.email as string),
  })
  const byLabel = (a: SelectOption, b: SelectOption) => a.label.localeCompare(b.label)
  const activeIds = new Set((usersStore.activeUsers ?? []).map((u) => u.id))

  const partners: SelectOption[] = []
  const staff: SelectOption[] = []
  const deactivated: SelectOption[] = []
  for (const u of users.value) {
    if (!activeIds.has(u.id)) deactivated.push(toOpt(u))
    else if (u.partner) partners.push(toOpt(u))
    else staff.push(toOpt(u))
  }

  const result: OptionGroup[] = []
  if (partners.length)
    result.push({ key: "partners", label: "Partners", options: partners.sort(byLabel) })
  if (staff.length) result.push({ key: "staff", label: "Staff", options: staff.sort(byLabel) })
  if (deactivated.length)
    result.push({ key: "deactivated", label: "Deactivated", options: deactivated.sort(byLabel) })
  return result
})

// Dispatch by field type; schema is the default.
const groups = computed<OptionGroup[]>(() => {
  switch (props.field.type) {
    case "type":
      return typeGroups.value
    case "user":
      // A user reference (Owner, an assignee) may be empty: "None" clears it.
      // Schema and type pickers keep no empty choice.
      return [
        { key: "none", label: null, options: [{ value: NONE, label: "None" }] },
        ...userGroups.value,
      ]
    default:
      return schemaGroups.value
  }
})

// Flattened options + a value→index map for keyboard nav and highlight.
const flatOptions = computed<SelectOption[]>(() => groups.value.flatMap((g) => g.options))
const indexByValue = computed(() => new Map(flatOptions.value.map((o, i) => [o.value, i] as const)))

const displayLabel = computed(() => {
  const v = currentValue.value
  if (v == null || v === "") return ""
  const idx = indexByValue.value.get(v)
  if (idx != null) return flatOptions.value[idx]!.label
  // Not in the current option set — fall back to the resolved entity's name.
  const resolved = currentEntity.value
  if (resolved) return String(resolved.name ?? resolved.label ?? v)
  return String(v)
})

// INTERACTION =================================================================

const dropdownRef = ref<HTMLElement | null>(null)
const highlightedIndex = ref(0)

// On edit-open, highlight the current value and focus the list for arrow keys.
watch(
  () => props.mode === "editing",
  (on) => {
    if (!on) return
    highlightedIndex.value =
      currentValue.value == null ? 0 : (indexByValue.value.get(currentValue.value) ?? 0)
    nextTick(() => dropdownRef.value?.focus())
  }
)

// Picking is atomic: emit the chosen value, then commit and close. The emitted
// value is the target ENTITY (uniform reference contract) — a minimal `{ id }`;
// the arbiter stores its id in the column/data.
function selectValue(value: OptionValue) {
  if (!indexByValue.value.has(value)) {
    emit("cancel")
    return
  }
  emit("update:modelValue", value === NONE ? null : { id: value })
  emit("commit")
}

function onDropdownKeydown(event: KeyboardEvent) {
  const opts = flatOptions.value
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault()
    event.stopPropagation()
    const opt = opts[highlightedIndex.value]
    if (opt) selectValue(opt.value)
    return
  }
  if (event.key === "ArrowDown") {
    event.preventDefault()
    event.stopPropagation()
    highlightedIndex.value = Math.min(highlightedIndex.value + 1, opts.length - 1)
    scrollHighlightedIntoView()
    return
  }
  if (event.key === "ArrowUp") {
    event.preventDefault()
    event.stopPropagation()
    highlightedIndex.value = Math.max(highlightedIndex.value - 1, 0)
    scrollHighlightedIntoView()
  }
}

function scrollHighlightedIntoView() {
  nextTick(() => {
    dropdownRef.value?.querySelector(".is-highlighted")?.scrollIntoView({ block: "nearest" })
  })
}
</script>

<style>
.select-reference-field {
  display: flex;
  width: 100%;
  align-items: center;
  cursor: default;

  .select-value {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .select-chevron {
    opacity: 0;
    margin-left: auto;
    flex-shrink: 0;
    cursor: pointer;
  }

  &:hover .select-chevron {
    opacity: 1;
  }
}

.grid-editor .select-reference-field {
  margin-top: -0.5px;
  margin-left: 1px;
}
.grid-editor .select-reference-field .select-chevron {
  opacity: 1;
}

/* Custom dropdown inside the floating `.grid-editor` box. Scoped under the field
   root so it stays independent of SelectValueField's parallel dropdown. */
.select-reference-field .select-dropdown {
  display: block;
  max-height: 240px;
  overflow-y: auto;
  outline: none;
  width: 100%;

  .select-group-label {
    padding: 4px 6px 2px;
    font-size: var(--auger-size-7);
    font-weight: 600;
    color: var(--auger-text-weak);
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }

  .select-option {
    padding: 4px 6px;
    cursor: pointer;

    &.is-highlighted {
      background: var(--auger-link, #485fc7);
      color: var(--auger-text-inverted);
      border-radius: var(--auger-radius);
    }

    &.is-selected {
      font-weight: 600;
    }
  }

  .select-empty {
    padding: 4px 6px;
    color: var(--auger-text-weak);
  }
}
</style>
